import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Transaction from '@/models/Transaction';
import CustomTooltip from '@/components/ui/Tooltip/Tooltip';
import tooltipInfo from '@/assets/icons/tooltip-info.svg';
import { formatSelectionAmount, summarizeSelectedTransactions } from './selectionTotals';
import styles from './SelectedTransactionsSummary.module.scss';

type Tone = 'income' | 'expense' | 'neutral';

const toneOf = (value: number): Tone => (value > 0 ? 'income' : value < 0 ? 'expense' : 'neutral');

const Amount = ({ value, currency, className }: { value: number; currency: string; className?: string }) => {
  const { i18n } = useTranslation();
  return (
    <bdi dir="ltr" className={`${styles.amount} ${className ?? ''}`} data-tone={toneOf(value)}>
      {formatSelectionAmount(value, currency, i18n.language)}
    </bdi>
  );
};

interface SelectedTransactionsSummaryProps {
  transactions: Transaction[];
  fundCurrency?: string | null;
}

/**
 * Lives in the board's selection bar. The net total is the figure a coordinator
 * ticks rows to get, so it is the largest thing here; the income/expense split
 * appears only when the selection actually mixes the two, and foreign movements
 * that could not be converted are listed apart so the main figure stays honest.
 */
const SelectedTransactionsSummary = ({ transactions, fundCurrency }: SelectedTransactionsSummaryProps) => {
  const { t } = useTranslation();
  const totals = useMemo(() => summarizeSelectedTransactions(transactions, fundCurrency), [transactions, fundCurrency]);
  const isMixed = totals.income > 0 && totals.expenses < 0;

  return (
    <div className={styles.summary} role="status" aria-live="polite" data-selected-total>
      <span className={styles.divider} aria-hidden="true">
        |
      </span>

      <div className={styles.net}>
        <span className={`body-sm-regular ${styles.label}`}>{t('selectedRowsTotal')}</span>
        <Amount value={totals.net} currency={totals.currency} className={styles.netAmount} />
        {totals.convertedCount > 0 && (
          <>
            <img id="selectedTotalConversionTooltip" src={tooltipInfo} alt={t('selectedRowsTotalConvertedNote')} width={16} className={styles.info} />
            <CustomTooltip targetId="selectedTotalConversionTooltip">
              <p className="body-xs-sm-regular">{t('selectedRowsTotalConvertedNote', { count: totals.convertedCount })}</p>
            </CustomTooltip>
          </>
        )}
      </div>

      {isMixed && (
        <div className={styles.breakdown}>
          <span className={`body-sm-regular ${styles.chip}`} data-tone="income">
            <span className={styles.chipLabel}>{t('selectedRowsIncome')}</span>
            <Amount value={totals.income} currency={totals.currency} />
          </span>
          <span className={`body-sm-regular ${styles.chip}`} data-tone="expense">
            <span className={styles.chipLabel}>{t('selectedRowsExpenses')}</span>
            <Amount value={totals.expenses} currency={totals.currency} />
          </span>
        </div>
      )}

      {totals.futureCount > 0 && (
        <div className={styles.breakdown}>
          <span className={`body-sm-regular ${styles.chip}`} data-tone="neutral" title={t('selectedRowsFutureNote', { count: totals.futureCount })}>
            <span className={styles.chipLabel}>{t('selectedRowsFuture', { count: totals.futureCount })}</span>
            <Amount value={totals.futureAmount} currency={totals.currency} />
          </span>
        </div>
      )}

      {totals.unconverted.length > 0 && (
        <div className={styles.breakdown}>
          {totals.unconverted.map((bucket) => (
            <span
              key={bucket.currency}
              className={`body-sm-regular ${styles.chip}`}
              data-tone="neutral"
              title={t('selectedRowsUnconvertedNote', { count: bucket.count, currency: bucket.currency })}
            >
              <span className={styles.chipLabel}>{t('selectedRowsUnconverted')}</span>
              <Amount value={bucket.amount} currency={bucket.currency} />
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default SelectedTransactionsSummary;
